import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { getSessionToken } from "../../hooks/useSession.js";
import {
  Breadcrumbs,
  Button,
  Card,
  CardDescription,
  CardTitle,
  EmptyState,
  LoadingStatus,
  Skeleton,
  SkeletonText,
  StatusBadge,
} from "../ui/index.js";
import { PortalPageIntro, PortalWatermarkIcon } from "./PortalVisuals.js";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

interface DownlineContract {
  id: string;
  carrier: string;
  imo: string;
  status: string;
  contractDate: string | null;
  statusUpdatedAt: string;
}

interface DownlineContractsView {
  agent: {
    id: string;
    email: string;
    displayName: string | null;
    joinedAt: string;
    commissionLevel: string | null;
  };
  summary: {
    contractCount: number;
    activeContractCount: number;
  };
  contracts: DownlineContract[];
}

function formatCarrier(value: string) {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function DownlineContractsSkeleton() {
  return (
    <section className="space-y-8" role="status" aria-busy="true">
      <LoadingStatus label="Loading contracts" />
      <div className="space-y-3 border-b-2 border-border-default pb-7">
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-12 w-64 max-w-full" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Skeleton className="h-24 w-full rounded-card" />
        <Skeleton className="h-24 w-full rounded-card" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} padding="lg" className="min-h-[200px] space-y-5 border-2">
            <div className="flex items-start justify-between">
              <SkeletonText width="w-28" />
              <Skeleton className="h-6 w-20 rounded-lg" />
            </div>
            <SkeletonText width="w-40" />
          </Card>
        ))}
      </div>
    </section>
  );
}

/** An upline's read-only view of one downline's contracts. */
export function PortalDownlineContractsPage() {
  const { downlineId } = useParams<{ downlineId: string }>();
  const token = getSessionToken();
  const [view, setView] = useState<DownlineContractsView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !downlineId) return;
    let cancelled = false;

    async function loadContracts() {
      try {
        setError(null);
        const response = await fetch(
          `${API_BASE}/api/agents/me/downlines/${encodeURIComponent(downlineId!)}/contracts`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await response.json();
        if (!response.ok || !json.success) {
          throw new Error(json.error || "Unable to load contracts");
        }
        if (!cancelled) setView(json.data as DownlineContractsView);
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load contracts");
        }
      }
    }

    void loadContracts();
    return () => {
      cancelled = true;
    };
  }, [token, downlineId]);

  if (view === null && !error) return <DownlineContractsSkeleton />;

  const agentName = view?.agent.displayName || view?.agent.email || "Downline";
  const pending = (view?.contracts ?? []).filter((contract) => contract.status === "PENDING").length;

  return (
    <section className="space-y-8" aria-labelledby="downline-contracts-title">
      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="contents"
      >
        <PortalPageIntro
          titleId="downline-contracts-title"
          title={agentName}
          action={
            view ? (
              <Link
                to={`/my-agent/change-request?agent=${encodeURIComponent(view.agent.id)}`}
                className="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-accent-strong bg-accent-strong px-6 py-3 text-sm font-bold text-on-accent hover:brightness-95"
              >
                Request a change <span aria-hidden>→</span>
              </Link>
            ) : undefined
          }
          breadcrumbs={
            <Breadcrumbs
              items={[
                { label: "Setup", to: "/my-agent" },
                { label: "Downlines", to: "/my-agent/downlines" },
                { label: "Contracts", current: true },
              ]}
            />
          }
        />
      </motion.header>

      {error ? (
        <Card padding="lg" className="border-status-rejected-border bg-status-rejected-bg">
          <CardTitle>Contracts are temporarily unavailable</CardTitle>
          <CardDescription className="mt-2">{error}</CardDescription>
          <Button className="mt-5" variant="secondary" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </Card>
      ) : (
        <>
          <Card padding="lg" className="flex flex-wrap items-center gap-x-8 gap-y-3 border-2">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-content-muted">Email</p>
              <p className="mt-1 text-sm font-medium text-content-primary">{view?.agent.email}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-content-muted">Commission</p>
              <p className="mt-1 text-sm font-medium text-content-primary">
                {view?.agent.commissionLevel ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-content-muted">Joined</p>
              <p className="mt-1 text-sm font-medium text-content-primary">
                {view ? new Date(view.agent.joinedAt).toLocaleDateString() : "—"}
              </p>
            </div>
          </Card>

          {view?.contracts.length === 0 ? (
            <EmptyState
              title="No contracts yet"
              description={`${agentName} has not started contracting. Their contracts will show up here once they apply.`}
            />
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <Card padding="lg" className="relative flex min-h-24 items-center gap-4 overflow-hidden border-2 bg-status-active-bg border-status-active-border">
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-surface-raised text-status-active-text" aria-hidden>
                    ✓
                  </span>
                  <div>
                    <p className="font-display text-3xl font-extrabold text-content-primary">
                      {view?.summary.activeContractCount ?? 0}
                    </p>
                    <p className="text-sm font-bold text-content-secondary">Active appointments</p>
                  </div>
                </Card>
                <Card padding="lg" className="flex min-h-24 items-center gap-4 border-2 bg-status-pending-bg border-status-pending-border">
                  <span className="grid h-12 w-12 place-items-center rounded-xl bg-surface-raised text-status-pending-text" aria-hidden>
                    ◷
                  </span>
                  <div>
                    <p className="font-display text-3xl font-extrabold text-content-primary">{pending}</p>
                    <p className="text-sm font-bold text-content-secondary">Appointments in progress</p>
                  </div>
                </Card>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {view?.contracts.map((contract, index) => (
                  <motion.article
                    key={contract.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.15) }}
                  >
                    <Card padding="lg" className="relative flex min-h-[200px] h-full flex-col gap-5 overflow-hidden border-2">
                      <div className="pointer-events-none absolute -bottom-8 -right-6 h-32 w-32 text-accent opacity-[0.07]" aria-hidden>
                        <PortalWatermarkIcon kind="contracts" />
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-lg font-semibold text-content-primary">{formatCarrier(contract.carrier)}</p>
                            </div>
                        <StatusBadge status={contract.status} size="sm" />
                      </div>
                      <p className="mt-auto text-xs leading-5 text-content-secondary">
                        {contract.contractDate
                          ? `Contracted ${new Date(contract.contractDate).toLocaleDateString()}`
                          : `Updated ${new Date(contract.statusUpdatedAt).toLocaleDateString()}`}
                      </p>
                    </Card>
                  </motion.article>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
